import { getRailsClient, getRailsFormClient } from "sj-ui-toolkit/src/client";
import { getSvcSettingsClient } from "sj-ui-toolkit/src/client/svcSettings";
import { setAdminInfos } from "sj-ui-toolkit/src/api/admin/settings";
import { getSiteSettings } from "sj-ui-toolkit/src/api/siteSettings";
import type { SiteSettings } from "sj-ui-toolkit/src/api/types/siteSettingTypes";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AdminSettings {
  numberOfTries?: string;
}

export interface LogoUploadResponse {
  smallLogoUrl?: string;
  thumbLogoUrl?: string;
}

export interface LevelData {
  id: number;
  name: string;
  description: string;
  rankName: string;
  variant?: string;
  mapId?: number;
}

export interface LevelsListData {
  belt?: LevelData[];
  climb?: LevelData[];
  level?: LevelData[];
}

export interface LevelUpdatePayload {
  id: number;
  name: string;
  description: string;
  variant: string;
  mapId: number;
}

// ---------------------------------------------------------------------------
// Admin settings
// ---------------------------------------------------------------------------

export const updateAdminInfo = async (info: Record<string, string>): Promise<unknown> => {
  const response = await setAdminInfos(info);
  return response;
};

// ---------------------------------------------------------------------------
// Site settings
// ---------------------------------------------------------------------------

export const fetchSiteSettings = async (): Promise<SiteSettings | null> => {
  const response = await getSiteSettings();
  return response?.site ?? null;
};

// ---------------------------------------------------------------------------
// Logo upload
// ---------------------------------------------------------------------------

export const uploadLogoImage = async (file: File): Promise<LogoUploadResponse> => {
  const API = "/api/internal_api/v1/admin/infos/upload_logo_image";
  const formData = new FormData();
  formData.append("logo_image", file);

  const client = getRailsFormClient();
  const { data } = await client.post(API, formData);

  return {
    smallLogoUrl: data?.smallLogoUrl,
    thumbLogoUrl: data?.thumbLogoUrl,
  };
};

export const clearLogoImage = async (): Promise<unknown> => {
  const API = "/api/internal_api/v1/admin/infos/set_default_logo";
  const { data } = await getRailsClient().put(API);
  return data;
};

// ---------------------------------------------------------------------------
// Cognito auth settings
// ---------------------------------------------------------------------------

export const COGNITO_AUTH_SETTINGS_GROUP = "idp.auth.cognito";
export const PASSWORD_AUTH_FIELD = "enablePasswordAuth";

export const fetchPasswordAuthEnabled = async (tenant: string): Promise<boolean> => {
  const settingsData = await getSvcSettingsClient().getTenantSettings(
    tenant,
    COGNITO_AUTH_SETTINGS_GROUP
  ).promise;

  const group = (settingsData?.groups ?? []).find(g => g.groupName === COGNITO_AUTH_SETTINGS_GROUP);
  const value = group?.values?.[PASSWORD_AUTH_FIELD];
  // Password auth stays on unless the tenant has explicitly turned it off.
  return typeof value === "boolean" ? value : true;
};
